import { StationData, Variable } from 'domain/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { decimals } from 'domain/utils';
import {
	faCloudRain,
	faDroplet,
	faLeaf,
	faSun,
	faTemperatureHalf,
	faWind,
} from '@fortawesome/free-solid-svg-icons';
import { getColor } from 'domain/utils/varColor.utils';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

export const DashboardSummary = ({
	id,
	lastData,
}: {
	id: number;
	lastData?: StationData;
}) => {
	const variables: Variable[] = [
		Variable.TAS,
		Variable.PR,
		Variable.SFCWIND,
		Variable.HURS,
		Variable.IR,
		Variable.LEAFWETNESS,
	];

	return (
		<div className={'h-18 flex w-full justify-between'}>
			{variables.map((v) => {
				return (
					<InfoComponent
						key={`dashboard-summary-${v}`}
						variable={v}
						force={['tas', 'pr'].includes(v)}
						lastData={lastData}
					/>
				);
			})}
		</div>
	);
};

export const InfoComponent = ({
	lastData,
	variable,
	force,
}: {
	lastData?: StationData;
	variable: Variable;
	force?: boolean;
}) => {
	const value = useMemo(() => {
		return lastData?.data[variable] ?? undefined;
	}, [lastData, variable]);

	const { t } = useTranslation('variables');

	if (!force && value === undefined) {
		return null;
	}

	return (
		<div
			className={
				'm-[2px] flex w-[6rem] flex-col justify-between overflow-hidden truncate'
			}
			data-variable={variable}>
			<span
				style={{
					color: value !== undefined ? getColor(value, variable) : '',
				}}
				className={'text-2xl font-light'}>
				{decimals(value, 1)}
				<span className='pl-1 text-xl'>
					{t(`variables:unit.${variable}`, { defaultValue: '' })}
				</span>
			</span>
			<span className={'h-4 min-h-4 w-full text-xs text-gray-500'}>
				<Subtitle variable={variable} lastData={lastData} />
			</span>
			<span className='mt-2 text-xs text-gray-700'>
				<Icon variable={variable} />
			</span>
		</div>
	);
};

const Icon = ({ variable }: { variable: Variable }) => {
	const { t } = useTranslation(['variables']);

	switch (variable) {
		case 'hurs':
			return (
				<span className='block w-full truncate'>
					<FontAwesomeIcon icon={faDroplet} /> {t(`variables:${variable}`)}
				</span>
			);
		case 'sfcwind':
			return (
				<span className='block w-full truncate'>
					<FontAwesomeIcon icon={faWind} /> {t(`variables:${variable}`)}
				</span>
			);
		case 'pr':
			return (
				<span className='block w-full truncate'>
					<FontAwesomeIcon icon={faCloudRain} /> {t(`variables:${variable}`)}
				</span>
			);
		case 'tas':
			return (
				<span className='block w-full truncate'>
					<FontAwesomeIcon icon={faTemperatureHalf} />{' '}
					{t(`variables:${variable}`)}
				</span>
			);
		case 'ir':
			return (
				<span className='block w-full truncate'>
					<FontAwesomeIcon icon={faSun} /> {t(`variables:${variable}`)}
				</span>
			);
		case 'leafwetness':
			return (
				<span className='block w-full truncate'>
					<FontAwesomeIcon icon={faLeaf} /> {t(`variables:${variable}`)}
				</span>
			);
	}
	return null;
};

const Subtitle = ({
	variable,
	lastData,
}: {
	variable: Variable;
	lastData?: StationData;
}) => {
	const { t } = useTranslation(['variables']);

	let text = '';
	switch (variable) {
		case 'sfcwind':
			text = `${t('variables:sfcwind')}: ${decimals(
				lastData?.data.sfcwind,
				1
			)} ${t('variables:unit.sfcwind')}`;
			break;
		case 'wgust':
			text = `${decimals(lastData?.data.wgust, 1)} ${t(
				'variables:unit.wgust'
			)}`;
			break;
		case 'hurs':
			text = `${lastData?.data.humidityMin ?? ''} - ${
				lastData?.data.humidityMax ?? ''
			} ${t('variables:unit.hurs')}`;
			break;
		case 'leafwetness':
			text = `min`;
			break;
		case 'pr':
			text = `${t('labels:last_24_hours')}`;
			break;
		case 'tas':
			text = `${decimals(lastData?.data.tasmin, 0) ?? '-'} ${t(
				'variables:unit.tas'
			)} / ${decimals(lastData?.data.tasmax, 0) ?? '-'} ${t(
				'variables:unit.tas'
			)}`;
			break;
	}

	return <span className='block w-full truncate'>{text}</span>;
};
